import { useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Send, Star, Heart, Lightbulb, Bug, Rocket } from "lucide-react";
import { MicButton } from "@/components/MicButton";

export const FEEDBACK_MODULES = [
  "Général", "IA / Répondre", "Analyser", "Créer", "Débat", "Coach",
  "Connaître vos droits", "Base de connaissances", "Bibliothèque", "Partenaires",
  "Courriers", "Espace Jeunes", "Espace Parents", "Communauté", "Recherche", "Autre",
];

const TYPES = [
  { id: "compliment", label: "J'aime", icon: Heart },
  { id: "suggestion", label: "Une idée", icon: Lightbulb },
  { id: "feature", label: "Fonctionnalité", icon: Rocket },
  { id: "bug", label: "Un bug", icon: Bug },
];

function deviceString() {
  const ua = navigator.userAgent;
  const mobile = /Mobi|Android|iPhone|iPad/i.test(ua);
  const os = /Android/i.test(ua) ? "Android" : /iPhone|iPad|iOS/i.test(ua) ? "iOS"
    : /Mac/i.test(ua) ? "macOS" : /Windows/i.test(ua) ? "Windows" : "Autre";
  return `${os} · ${mobile ? "Mobile" : "Ordinateur"} · ${window.innerWidth}x${window.innerHeight}`;
}

export default function FeedbackForm({ defaultType = "suggestion", onDone }) {
  const { user } = useAuth();
  const [type, setType] = useState(defaultType);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [module, setModule] = useState("Général");
  const [liked, setLiked] = useState("");
  const [disliked, setDisliked] = useState("");
  const [improvement, setImprovement] = useState("");
  const [description, setDescription] = useState("");
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image trop lourde (max 5 Mo).");
    const r = new FileReader();
    r.onload = () => setShot(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    const hasText = liked || disliked || improvement || description;
    if (!rating && !hasText) return toast.error("Donnez une note ou écrivez quelques mots 🙂");
    setBusy(true);
    try {
      await api.post("/feedback", {
        type, rating: rating || null, module, liked, disliked, improvement, description,
        screenshot_base64: shot || "", device: deviceString(), page: window.location.pathname,
      });
      toast.success("Merci beaucoup ! Votre avis nous aide à construire BIZARRE AI 💛");
      setRating(0); setModule("Général"); setLiked(""); setDisliked("");
      setImprovement(""); setDescription(""); setShot(null);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const improvementPlaceholder = type === "feature"
    ? "🚀 Quelle nouvelle fonctionnalité aimeriez-vous ?"
    : "💡 Votre idée d'amélioration";

  return (
    <div data-testid="feedback-form">
      {/* Type */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`feedback-type-${t.id}`} onClick={() => setType(t.id)}
              className={`inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm border transition-colors ${
                type === t.id ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-white/60 hover:text-white"}`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      <label className="block text-sm text-white/70 mb-2">Note globale de l'application</label>
      <div className="flex gap-1.5 mb-5" data-testid="feedback-rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`feedback-star-${n}`} onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110">
            <Star size={28} className={(hover || rating) >= n ? "fill-gold text-gold" : "text-white/25"} />
          </button>
        ))}
      </div>

      <label className="block text-sm text-white/70 mb-2">Module concerné</label>
      <select data-testid="feedback-module" value={module} onChange={(e) => setModule(e.target.value)}
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm mb-4">
        {FEEDBACK_MODULES.map((m) => <option key={m} value={m} className="bg-[#0a0a0a]">{m}</option>)}
      </select>

      <textarea data-testid="feedback-liked" rows={2} value={liked} onChange={(e) => setLiked(e.target.value)}
        placeholder="👍 Ce que vous avez aimé"
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-none placeholder:text-white/30" />
      <div className="flex justify-end mt-1 mb-3"><MicButton testid="feedback-liked-mic" value={liked} onChange={setLiked} /></div>
      <textarea data-testid="feedback-disliked" rows={2} value={disliked} onChange={(e) => setDisliked(e.target.value)}
        placeholder="👎 Ce qui vous a dérangé"
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-none placeholder:text-white/30" />
      <div className="flex justify-end mt-1 mb-3"><MicButton testid="feedback-disliked-mic" value={disliked} onChange={setDisliked} /></div>
      <textarea data-testid="feedback-improvement" rows={2} value={improvement} onChange={(e) => setImprovement(e.target.value)}
        placeholder={improvementPlaceholder}
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-none placeholder:text-white/30" />
      <div className="flex justify-end mt-1 mb-3"><MicButton testid="feedback-improvement-mic" value={improvement} onChange={setImprovement} /></div>
      <textarea data-testid="feedback-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="🐞 Signaler un bug (décrivez ce qui ne fonctionne pas)"
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-none placeholder:text-white/30" />
      <div className="flex justify-end mt-1 mb-3"><MicButton testid="feedback-description-mic" value={description} onChange={setDescription} /></div>

      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="feedback-file" />
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => fileRef.current?.click()} data-testid="feedback-upload"
          className="inline-flex items-center gap-2 border border-white/15 rounded-full px-3 py-1.5 text-sm hover:bg-white/[0.06]">
          <Upload size={14} /> Capture d'écran
        </button>
        {shot && <img src={shot} alt="capture" className="h-10 rounded" />}
      </div>

      <div className="text-xs text-white/35 mb-5 space-y-0.5">
        <p data-testid="feedback-device">📱 {deviceString()}</p>
        {user?.email && <p data-testid="feedback-email">📧 {user.email}</p>}
      </div>

      <button data-testid="feedback-submit" onClick={submit} disabled={busy}
        className="w-full gold-gradient text-black rounded-full py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-50">
        <Send size={16} /> {busy ? "Envoi…" : "Envoyer mon avis"}
      </button>
    </div>
  );
}
